import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useNetwork } from '@/contexts/NetworkContext';
import { logPrefetch, offlineLog } from '@/utils/offlineDebug';

const COMPONENT = 'BranchContext';

interface BranchContextType {
  selectedBranchId: string | null;
  setSelectedBranchId: (branchId: string | null) => void;
  /** Force prefetch menu data for the current branch (useful for retry) */
  prefetchCurrentBranch: () => Promise<void>;
  /** Whether menu data has been prefetched for the current branch */
  isPrefetched: boolean;
  /** Whether branch selection has been validated against user's available branches */
  isValidated: boolean;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

const BRANCH_STORAGE_KEY = 'menu-maven-selected-branch';
const PREFETCH_STATUS_KEY = 'menu-maven-prefetch-status';

// Max retries for startup prefetch
const MAX_PREFETCH_RETRIES = 5;
const PREFETCH_RETRY_DELAY_MS = 2000; // 2 seconds between retries

export const BranchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { branches, loading: branchesLoading } = useFilteredBranches();
  
  // Don't read from localStorage immediately - wait for validation
  const [selectedBranchId, setSelectedBranchIdState] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  
  // Track prefetch status per branch
  const [isPrefetched, setIsPrefetched] = useState(false);
  const prefetchInProgress = useRef(false);
  const lastPrefetchedBranch = useRef<string | null>(null);
  const wasOffline = useRef(false);
  const prefetchRetryCount = useRef(0);
  const prefetchRetryTimer = useRef<NodeJS.Timeout | null>(null);
  const startupPrefetchDone = useRef(false);

  // SECURITY FIX: Validate stored branch ID belongs to current user's branches
  // This prevents data leakage between different users/tenants
  useEffect(() => {
    // Wait for branches to finish loading
    if (branchesLoading) {
      return;
    }
    
    // Get stored branch ID from localStorage
    const storedBranchId = localStorage.getItem(BRANCH_STORAGE_KEY);
    
    if (branches.length === 0) {
      // User has no branches - clear any stored value and set to null
      if (storedBranchId) {
        console.log('[BranchContext] No branches available, clearing stored branch ID');
        localStorage.removeItem(BRANCH_STORAGE_KEY);
      }
      setSelectedBranchIdState(null);
      setIsValidated(true);
      return;
    }
    
    // Validate stored branch exists in user's branches
    if (storedBranchId) {
      const branchExists = branches.some(b => b.id === storedBranchId);
      if (branchExists) {
        // Stored branch is valid for this user
        console.log('[BranchContext] Validated stored branch:', storedBranchId);
        setSelectedBranchIdState(storedBranchId);
      } else {
        // SECURITY: Stored branch doesn't belong to this user - select first available
        console.warn('[BranchContext] SECURITY: Stored branch ID does not belong to current user, resetting');
        const firstBranch = branches[0];
        setSelectedBranchIdState(firstBranch.id);
        localStorage.setItem(BRANCH_STORAGE_KEY, firstBranch.id);
      }
    } else {
      // No stored branch - auto-select first one
      const firstBranch = branches[0];
      console.log('[BranchContext] No stored branch, selecting first:', firstBranch.id);
      setSelectedBranchIdState(firstBranch.id);
      localStorage.setItem(BRANCH_STORAGE_KEY, firstBranch.id);
    }
    
    setIsValidated(true);
  }, [branches, branchesLoading]);

  // Prefetch menu data when branch changes (Electron desktop only)
  const { isElectronApp, isOnline, prefetchMenu } = useNetwork();
  
  // Store prefetchMenu in a ref to avoid dependency issues
  const prefetchMenuRef = useRef(prefetchMenu);
  useEffect(() => {
    prefetchMenuRef.current = prefetchMenu;
  }, [prefetchMenu]);
  
  // Cleanup retry timer on unmount
  useEffect(() => {
    return () => {
      if (prefetchRetryTimer.current) {
        clearTimeout(prefetchRetryTimer.current);
      }
    };
  }, []);
  
  // Prefetch function that can be called manually or automatically
  // Returns true if successful, false otherwise (for retry logic)
  const doPrefetch = useCallback(async (branchId: string, reason: string): Promise<boolean> => {
    if (!isElectronApp) {
      offlineLog('info', `Skip prefetch (${reason}): not Electron`, { component: COMPONENT, branchId });
      return false;
    }
    
    if (!isOnline) {
      offlineLog('info', `Skip prefetch (${reason}): offline`, { component: COMPONENT, branchId });
      return false;
    }
    
    if (prefetchInProgress.current) {
      offlineLog('info', `Skip prefetch (${reason}): already in progress`, { component: COMPONENT, branchId });
      return false;
    }
    
    prefetchInProgress.current = true;
    logPrefetch('start', branchId);
    
    try {
      const result = await prefetchMenuRef.current(branchId);
      
      if (result?.success) {
        lastPrefetchedBranch.current = branchId;
        startupPrefetchDone.current = true;
        prefetchRetryCount.current = 0; // Reset retry count on success
        setIsPrefetched(true);
        // Store prefetch status in localStorage
        const prefetchStatus = JSON.parse(localStorage.getItem(PREFETCH_STATUS_KEY) || '{}');
        prefetchStatus[branchId] = Date.now();
        localStorage.setItem(PREFETCH_STATUS_KEY, JSON.stringify(prefetchStatus));
        
        logPrefetch('success', branchId, {
          categoriesCount: result.categoriesCount,
          itemsCount: result.itemsCount
        });
        return true;
      } else {
        logPrefetch('error', branchId, { error: result?.error || 'Unknown error' });
        return false;
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Unknown error';
      logPrefetch('error', branchId, { error });
      return false;
    } finally {
      prefetchInProgress.current = false;
    }
  }, [isElectronApp, isOnline]);
  
  // Track retry state to trigger re-renders
  const [retryTrigger, setRetryTrigger] = useState(0);
  
  // Effect 1: Startup prefetch with retry logic
  // This ensures menu is cached even if initial conditions aren't ready
  useEffect(() => {
    if (!selectedBranchId || !isElectronApp) {
      return;
    }
    
    // If already done startup prefetch, skip
    if (startupPrefetchDone.current && lastPrefetchedBranch.current === selectedBranchId) {
      return;
    }
    
    // If not online yet, schedule a retry
    if (!isOnline) {
      if (prefetchRetryCount.current < MAX_PREFETCH_RETRIES) {
        offlineLog('info', `Not online yet, scheduling prefetch retry ${prefetchRetryCount.current + 1}/${MAX_PREFETCH_RETRIES}`, { component: COMPONENT, branchId: selectedBranchId });
        
        if (prefetchRetryTimer.current) {
          clearTimeout(prefetchRetryTimer.current);
        }
        
        prefetchRetryTimer.current = setTimeout(() => {
          prefetchRetryCount.current++;
          setRetryTrigger(prev => prev + 1); // Force a re-render to retry
        }, PREFETCH_RETRY_DELAY_MS);
      }
      return;
    }
    
    // We're online and have a branch - do the prefetch
    doPrefetch(selectedBranchId, 'startup').then(success => {
      if (!success && prefetchRetryCount.current < MAX_PREFETCH_RETRIES) {
        // Schedule retry on failure
        offlineLog('info', `Prefetch failed, scheduling retry ${prefetchRetryCount.current + 1}/${MAX_PREFETCH_RETRIES}`, { component: COMPONENT, branchId: selectedBranchId });
        
        prefetchRetryTimer.current = setTimeout(() => {
          prefetchRetryCount.current++;
          lastPrefetchedBranch.current = null; // Allow retry
          setRetryTrigger(prev => prev + 1); // Force a re-render to retry
        }, PREFETCH_RETRY_DELAY_MS);
      }
    });
  }, [selectedBranchId, isElectronApp, isOnline, doPrefetch, retryTrigger]);
  
  // Effect 2: Re-prefetch when coming back online
  useEffect(() => {
    if (isElectronApp) {
      if (!isOnline) {
        wasOffline.current = true;
      } else if (wasOffline.current && selectedBranchId) {
        wasOffline.current = false;
        // Force re-prefetch when coming back online
        offlineLog('success', `Back online - re-prefetching menu for branch: ${selectedBranchId}`, { component: COMPONENT, branchId: selectedBranchId });
        lastPrefetchedBranch.current = null; // Reset to force refetch
        doPrefetch(selectedBranchId, 'reconnected');
      }
    }
  }, [isElectronApp, isOnline, selectedBranchId, doPrefetch]);
  
  // Exposed function to manually trigger prefetch
  const prefetchCurrentBranch = useCallback(async () => {
    if (selectedBranchId) {
      lastPrefetchedBranch.current = null; // Force refetch
      await doPrefetch(selectedBranchId, 'manual');
    }
  }, [selectedBranchId, doPrefetch]);

  const setSelectedBranchId = (branchId: string | null) => {
    setSelectedBranchIdState(branchId);
    setIsPrefetched(false); // Reset prefetch status on branch change
    lastPrefetchedBranch.current = null;
    if (branchId) {
      localStorage.setItem(BRANCH_STORAGE_KEY, branchId);
    } else {
      localStorage.removeItem(BRANCH_STORAGE_KEY);
    }
  };

  // Only expose selectedBranchId after validation to prevent data leakage
  const safeSelectedBranchId = isValidated ? selectedBranchId : null;

  return (
    <BranchContext.Provider value={{ 
      selectedBranchId: safeSelectedBranchId, 
      setSelectedBranchId, 
      prefetchCurrentBranch, 
      isPrefetched,
      isValidated
    }}>
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = (optional = false): BranchContextType => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    if (optional) {
      return {
        selectedBranchId: null,
        setSelectedBranchId: () => {},
        prefetchCurrentBranch: async () => {},
        isPrefetched: false,
        isValidated: true
      };
    }
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};
